// chat/Chat.tsx — the chat panel UI (fleet App-Brain pattern, adapted to Oriora Study).
// Purely ADDITIVE: it drives the SAME capabilities the app already has (add subject / log
// study time / search the library / delete / explain) by typing. All styling is namespaced
// under .rbc-* (the scaffold's chat classes) so it can't collide with the app CSS.
//
// HITL: a delete proposed by the router is held in `pending` and only runs on an explicit
// Confirm tap (a chat message can't destroy a library entry on its own) — fleet discipline.

import { useState, useRef, useEffect } from "react";
import { askBrain, type ChatTurn } from "./brain";
import { MicButton, SpeakButton } from "./voice";
import { PhotoButton } from "./photo";
import { cloudBackupStatus } from "../brain";
import { executeAction, rowLabel, type ChatAction, type FilterRow } from "./actions";
import CallOverlay from "../live/CallOverlay";

const STORE_KEY = "oriora.study.chat.v1"; // device-only; lost on clear/reinstall

const GREETING: ChatTurn = {
  role: "assistant",
  content:
    // ⚠️ Never promise the customer that something "never leaves the device" — we promise only
    // that Oriora doesn't RETAIN it. (Locked by Bogdan 2026-07-10, fleet-wide.) Here the photo
    // genuinely is read on-device and only its text is sent, so say exactly that and no more.
    "Tell me what to do — I'll make notes from your material, build a study plan, explain a concept, log study time, or find anything in your library. Tap 📷 to photograph a page: it's read on your device, and only the text it finds goes into the chat.\n\n" +
    'e.g. "make notes from this chapter" · "plan for my biology exam on 12 May" · "explain osmosis" · "log 45 minutes of biology" · "show my plans".',
};

function loadChat(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed) && parsed.length) return parsed; }
  } catch { /* ignore — start fresh */ }
  return [GREETING];
}
function saveChat(turns: ChatTurn[]) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100))); } catch { /* ignore */ }
}

export default function Chat({ onChanged }: { onChanged?: () => void }) {
  const [turns, setTurns] = useState<ChatTurn[]>(loadChat);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  // A delete is held here until the user taps Confirm — the router proposing it is NOT enough.
  const [pending, setPending] = useState<ChatAction | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => { saveChat(turns); }, [turns]);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, onChunk);
      const transcript = next.map((t) => `${t.role}: ${t.content}`).join("\n");
      // Destructive actions are NEVER auto-run from a chat turn — they wait for a Confirm tap.
      const destructive = (k: ChatAction["kind"]) => k === "backup_restore" || k === "import_file" || k === "delete_artifact" ||
        k === "library_remove" || k === "library_merge";

      // Run every non-destructive action in order — "digest ch.3 and ch.4" must do BOTH.
      const lines: string[] = [];
      let rows: FilterRow[] | undefined;
      let changed = false;
      for (let action of actions) {
        if (destructive(action.kind)) {
          // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
          // resolve the priority source (what the panel's status line shows) before the hold.
          if (action.kind === "backup_restore" && !action.provider) {
            try {
              const s = await cloudBackupStatus();
              if (s.writes_to) action = { ...action, provider: s.writes_to };
            } catch { /* hold falls back to "cloud" wording */ }
          }
          // Hold the FIRST destructive action for confirmation; report what already ran.
          setPending(action);
          const warn = action.kind === "import_file"
            ? "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel."
            : action.kind === "backup_restore"
              ? `Restore will REPLACE everything in the app with your ${action.provider === "gdrive" ? "Google Drive" : action.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`
              : `Delete ${action.kind === "delete_artifact" ? (action.label ?? "this entry") : "this"} from your library? This can't be undone — tap Confirm, or Cancel to keep it.`;
          setTurns([...next, { role: "assistant", content: [reply, ...lines, warn].filter(Boolean).join("\n\n"), rows }]);
          if (changed) onChanged?.();
          setBusy(false);
          return;
        }
        const result = await executeAction(action, { transcript });
        if (result.message) lines.push(result.message);
        if (result.rows) rows = result.rows;
        if (result.changed) changed = true;
      }
      const body = [reply, ...lines].filter(Boolean).join("\n\n");
      setTurns([...next, { role: "assistant", content: body || "Done.", rows }]);
      if (changed) onChanged?.();
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending || busy) return;
    setBusy(true);
    try {
      const result = await executeAction(pending, { transcript: turns.map((t) => `${t.role}: ${t.content}`).join("\n") });
      setTurns((t) => [...t, { role: "assistant", content: result.message || "Done." }]);
      if (result.changed) onChanged?.();
    } catch (e) {
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setPending(null);
      setBusy(false);
    }
  }

  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([GREETING]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    setPending(null);
    setTurns((t) => [...t, { role: "assistant", content: "Okay — kept it. Tell me what else to do." }]);
  }

  return (
    <div className="rbc-chat">
      <div className="rbc-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`rbc-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`rbc-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.rows && t.rows.length > 0 && <FilterRows rows={t.rows} />}
              {t.role !== "user" && t.content.trim() ? (<div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>) : null}
            </div>
          </div>
        ))}
        {busy && <div className="rbc-msgrow"><div className="rbc-bubble bot thinking">thinking</div></div>}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="rbc-composer rbc-confirm">
          <button className="btn btn-danger btn-pill" onClick={confirmPending} disabled={busy}>{pending?.kind && String(pending.kind).startsWith("delete") ? "Confirm delete" : "Confirm"}</button>
          <button className="btn btn-pill" onClick={cancelPending} disabled={busy}>Cancel</button>
        </div>
      ) : (
        <div className="rbc-composer">
          <textarea
            value={input}
            placeholder="Tell me what to do…"
            rows={2}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          {/* Photo IN, next to the mic (hard gate). The page is OCR'd on-device; only its TEXT
              enters the conversation, so nothing is billed and no image leaves the phone. */}
          <PhotoButton
            onNotice={(msg) => setTurns((t) => [...t, { role: "assistant", content: msg }])}
            onText={(t) => setInput((v) => (v ? v + "\n\n" + t : t))}
            disabled={busy}
          />
          <MicButton onText={(t) => setInput((v) => (v ? v + " " : "") + t)} disabled={busy} />
            {/* Every surface. iOS runs the native voice plugin; macOS and web use the browser's
                own audio (getUserMedia + its echo canceller) — the stack every desktop voice
                assistant uses. The gate here used to be iOS-only because no other path existed. */}
            {<button
              type="button"
              className="btn btn-pill"
              title="Talk to it live"
              aria-label="Start a live call"
              style={{ flexShrink: 0, fontSize: 16 }}
              onClick={() => setOnCall(true)}
            >📞</button>}
            {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position
                sheet, so it renders correctly anywhere in the tree, and this needs no
                structural analysis of each app's JSX — a button whose overlay never got
                mounted is the half-wired failure this roll exists to avoid. */}
            {onCall && <CallOverlay
              onClose={() => setOnCall(false)}
              onTranscript={(callLines, seconds) => {
                const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
                setTurns((prev) => [
                  ...prev,
                  { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                  ...callLines.map((l) => ({
                    role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                    content: l.text.trim(),
                  })),
                ]);
              }}
            />}
          <button className="btn btn-primary btn-pill" onClick={send} disabled={busy}>Send</button>
        </div>
      )}
    </div>
  );
}

function FilterRows({ rows }: { rows: FilterRow[] }) {
  return (
    <div className="rbc-rows">
      {rows.map((r) => (
        <div key={r.id} className="rbc-rowitem">
          <span>{r.title}</span>
          <span className="muted" style={{ fontSize: "var(--fs-xs)", whiteSpace: "nowrap" }}>
            {rowLabel(r)} · {r.created}
          </span>
        </div>
      ))}
    </div>
  );
}
